import React, { useEffect, useState } from "react";
import { Paper, Grid, TextField, Button, Box, useTheme, useMediaQuery, ButtonGroup } from "@mui/material";
import { AttachFile, Description, Settings } from "@mui/icons-material";
import ContentCutIcon from '@mui/icons-material/ContentCut';
import AddLinkIcon from '@mui/icons-material/AddLink';
import { SendEmail } from "./panelNotes/SendEmail";
import { NotesTable } from "./panelNotes/NotesTable";
import { ClippingDownload } from "./panelNotes/DownloadClipping";
import { ClippingSettingsModal } from "./panelNotes/ClippingSettings";
import GenerateClipping from "./panelNotes/GenerateClipping";
import { DownloadWordDocumentModal } from "./panelNotes/DownloadWordDocumentModal";
import { useFetchNotas } from "../../hooks/useGetNotes";
import { useFetchSections } from "../../hooks/useGetSections";
import { useGenerateClipping } from "../../hooks/useClipping/usePostGenerateClipping";
import { Loading } from "../sharedComponents/Loading";
import { DataNotFound } from "../sharedComponents/DataNotFound";
import { SectionList } from "../sharedComponents/SectionList";
import { useStyles } from "../../styles/Styles";
import { postSectionClippingURL } from "../../utils/ConstantsURL";

export const NotesPanel = ({ token, date, idDocument, nameDocument }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState(date);
  const [modalEmail, setModalEmail] = useState(false);
  const [selectedIdSection, setSelectedIdSection] = useState(null);
  const [selectedNameSection, setSelectedNameSection] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showGenerateClipping, setShowGenerateClipping] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadQueue, setDownloadQueue] = useState([]);
  const [hiperlyncQueue, setHiperlyncQueue] = useState([]);
  const [configModal, setConfigModal] = useState(false);
  const [wordModalOpen, setWordModalOpen] = useState(false);

  const classes = useStyles();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("lg"));

  const { sections, loading } = useFetchSections(token, idDocument);
  const { notas, loadingNote } = useFetchNotas(
    token,
    selectedDate,
    selectedIdSection,
    refresh,
    idDocument
  );

  useEffect(() => {
    if (sections.length > 0 && !selectedIdSection) {
      setSelectedIdSection(sections[0]?.ID_SECCION);
    }
  }, [sections, selectedIdSection]);

  const handleSelect = (id, name) => {
    setSelectedIdSection(id);
    setSelectedNameSection(name);
    setRefresh((prev) => !prev);
  };

  const filteredData = notas.filter((item) =>
    item.titulo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const updateDownloadQueue = (file) => {
    setDownloadQueue((prevQueue) => {
      const index = prevQueue.findIndex((item) => item.id === file.id);
      if (index !== -1) {
        const updatedQueue = [...prevQueue];
        updatedQueue[index] = { ...updatedQueue[index], ...file };
        return updatedQueue;
      }
      return [...prevQueue, file];
    });
  };

  const addToHiperlyncQueue = (file) => {
    setHiperlyncQueue((prevQueue) => [...prevQueue, file]);
  };

  const { generateClipping } = useGenerateClipping(token, updateDownloadQueue, addToHiperlyncQueue);

  return (
    <Paper elevation={0} style={{ height: "100%" }}>
      {loading ? (
        <Loading />
      ) : (
        <Grid
          container
          spacing={2}
          style={{
            height: '100%',
            ...(isXs ? { overflowY: 'auto' } : {}),
          }}
        >
          <Grid item xs={12} container spacing={2}>
            <Grid item xs={12} sm={3} md={3}>
              <TextField
                label="Fecha"
                type="date"
                variant="outlined"
                value={selectedDate}
                onChange={(event) => setSelectedDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "40px",
                    borderRadius: "12px",
                    fontSize: "14px",
                    padding: "8px",
                  },
                  "& .MuiInputBase-input": {
                    padding: "8px",
                  }
                }}
                inputProps={{
                  max: new Date().toISOString().split("T")[0],
                }}
              />
            </Grid>
            <Grid item xs={6} sm={2} md={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setModalEmail(!modalEmail)}
                fullWidth
                sx={{ borderRadius: "12px" }}
              >
                Enviar Email
              </Button>
            </Grid>
            <Grid item xs={6} sm={2} md={2}>
              <Button
                variant="contained"
                color="success"
                onClick={() => setWordModalOpen(true)}
                fullWidth
                sx={{ borderRadius: "12px" }}
              >
                <Description sx={{ mr: 1 }} />
                Descargar
              </Button>
            </Grid>
            <Grid item xs={6} sm={2} md={2}>
              <ButtonGroup fullWidth>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setShowGenerateClipping(true)}
                  sx={{
                    borderTopLeftRadius: "12px",
                    borderBottomLeftRadius: "12px",
                    flex: 2
                  }}
                >
                  <AttachFile sx={{ mr: 1 }} />
                  Generar
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setConfigModal(true)}
                  sx={{
                    borderTopRightRadius: "12px",
                    borderBottomRightRadius: "12px",
                    flex: 1,
                    minWidth: "40px"
                  }}
                >
                  <Settings />
                </Button>
              </ButtonGroup>
            </Grid>
            <Grid item xs={6} sm={3} md={3}>
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                onClick={() => setShowDownloadModal(true)}
                sx={{
                  borderRadius: "12px",
                  position: "relative",
                }}
              >
                Descargas
                {downloadQueue.length > 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 5,
                      right: 5,
                      backgroundColor: "red",
                      color: "white",
                      borderRadius: "50%",
                      width: 20,
                      height: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                    }}
                  >
                    {downloadQueue.length}
                  </Box>
                )}
                {hiperlyncQueue.length > 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 2,
                      right: 2,
                      backgroundColor: "blue",
                      color: "white",
                      borderRadius: "50%",
                      width: 10,
                      height: 10,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "bold",
                    }}
                  />
                )}
              </Button>
            </Grid>
          </Grid>
          <Grid item xs={12} container spacing={2} style={{ height: "100%", marginTop: 1 }}>
            <Grid item xs={12} lg={2} style={{ maxHeight: "480px", overflowY: "auto", paddingTop: 0 }}>
              <SectionList
                sections={sections}
                selectedIdSection={selectedIdSection}
                handleSelect={handleSelect}
              />
            </Grid>
            <Grid item xs={12} lg={10} style={{ height: "85%", paddingTop: 0 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                  <TextField
                    label="Buscar notas por título"
                    variant="outlined"
                    fullWidth
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "40px",
                        borderRadius: "12px",
                        fontSize: "14px",
                        padding: "8px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "8px",
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={6} sm={3} md={3}>
                  <Button
                    fullWidth
                    onClick={() => setShowDetails(!showDetails)}
                    variant="outlined"
                    color="primary"
                    sx={{
                      borderRadius: "12px",
                      color: theme.palette.mode === "dark" ? "#fff" : "",
                      borderColor:
                        theme.palette.mode === "dark" ? "#fff" : "",
                    }}
                  >
                    {showDetails ? "Ocultar síntesis" : "Mostrar síntesis"}
                  </Button>
                </Grid>
                <Grid item xs={6} sm={3} md={3}>
                  <ButtonGroup fullWidth>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() =>
                        generateClipping(
                          idDocument,
                          selectedDate,
                          selectedNameSection,
                          true,
                          [selectedIdSection],
                          () => { },
                          {
                            cover: false,
                            index: false,
                            dividers: true,
                          },
                          false,
                          postSectionClippingURL
                        )
                      }
                      sx={{
                        borderTopLeftRadius: "12px",
                        borderBottomLeftRadius: "12px",
                        flex: 1
                      }}
                    >
                      <ContentCutIcon />
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() =>
                        generateClipping(
                          idDocument,
                          selectedDate,
                          selectedNameSection,
                          true,
                          [selectedIdSection],
                          () => { },
                          {
                            cover: false,
                            index: false,
                            dividers: true,
                          },
                          true,
                          postSectionClippingURL
                        )
                      }
                      sx={{
                        borderTopRightRadius: "12px",
                        borderBottomRightRadius: "12px",
                        flex: 1,
                        minWidth: "40px"
                      }}
                    >
                      <AddLinkIcon />
                    </Button>
                  </ButtonGroup>
                </Grid>
              </Grid>
              <Box style={{ height: "100%", marginTop: 10 }}>
                {loadingNote ? (
                  <Loading message="Cargando Notas..." />
                ) : filteredData.length === 0 ? (
                  <DataNotFound />
                ) : (
                  <NotesTable
                    classes={classes}
                    filteredData={filteredData}
                    date={selectedDate}
                    token={token}
                    refresh={() => setRefresh(!refresh)}
                    showDetails={showDetails}
                    selectedIdSection={selectedIdSection}
                    idDocument={idDocument}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </Grid>
      )}
      <SendEmail
        token={token}
        open={modalEmail}
        close={() => setModalEmail(!modalEmail)}
        idDocument={idDocument}
        refresh={() => setRefresh(!refresh)}
      />
      <GenerateClipping
        open={showGenerateClipping}
        onClose={() => setShowGenerateClipping(false)}
        token={token}
        id_documento={idDocument}
        nameDocument={nameDocument}
        fecha={selectedDate}
        addToDownloadQueue={updateDownloadQueue}
        addToHiperlyncQueue={addToHiperlyncQueue}
      />
      <ClippingDownload
        open={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        downloadQueue={downloadQueue}
        setDownloadQueue={setDownloadQueue}
        hiperlync={true}
        token={token}
        selectedDate={selectedDate}
        setHiperlyncQueue={setHiperlyncQueue}
        hiperlyncQueue={hiperlyncQueue}
      />
      <ClippingSettingsModal
        open={configModal}
        onClose={() => setConfigModal(false)}
        token={token}
        idDocument={idDocument}
        nameDocument={nameDocument}
        sections={sections}
        fecha={selectedDate}
        addToDownloadQueue={updateDownloadQueue}
        addToHiperlyncQueue={addToHiperlyncQueue}
      />

      <DownloadWordDocumentModal
        token={token}
        open={wordModalOpen}
        close={() => setWordModalOpen(false)}
        documentData={{
          id_documento: idDocument,
          fecha: selectedDate,
          nombreDocumento: nameDocument 
        }}
      />

    </Paper>
  );
};
